/* eslint-disable unused-imports/no-unused-imports */
/* eslint-disable @typescript-eslint/no-unused-vars */
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faChevronLeft,
  faChevronRight,
} from '@fortawesome/sharp-solid-svg-icons'
import { ActionIcon, Group } from '@mantine/core'
import { first, get, last, noop } from 'lodash'
import moment, { Moment } from 'moment'
import React, { useEffect, useState } from 'react'
import { Button } from 'react-bootstrap'
import DayPicker from 'react-day-picker'
import { BeforeAfterModifier, DayModifiers } from 'react-day-picker/types'

import Button1stlog from '../../Button'
import Icon from '../../Icon'
import Overlay from '../../Overlay'
import {
  getMonthsLong,
  getMonthYearOptions,
  getWeekdaysLong,
  getWeekdaysShort,
} from '../constants'
import DateInput from '../DateInput'
import { TParseDate, TParseDateObject } from '../helpers'
import { TDatePicker } from '../index'
import MonthYearPicker from '../MonthYearPicker'
import Presets from '../Presets'
import TimePicker from '../TimePicker'
import CustomNavbar from './CustomNavbar'

interface TDatePickerProps
  extends Pick<
    TDatePicker,
    | 'isRange'
    | 'nodisabled'
    | 'validateTime'
    | 'autoclose'
    | 'timepicker'
    | 'min'
    | 'max'
    | 'clearable'
  > {
  value: TParseDate
  onChange: (data: TParseDate) => void
  onClose: () => void
}

const DatePicker = (props: TDatePickerProps): React.ReactElement => {
  const {
    value,
    onChange = noop,
    onClose = noop,
    min,
    max,
    isRange = false,
    autoclose,
    timepicker = false,
    nodisabled,
    validateTime = false,
    clearable,
  } = props

  // daterange state in moment()
  const [start, setStart] = useState<Moment | undefined>()
  const [end, setEnd] = useState<Moment>()
  const [startTime, setStartTime] = useState<Moment>()
  const [endTime, setEndTime] = useState<Moment>()
  const [hoveredDay, setHoveredDay] = useState<Moment>()
  const [displayedMonth, setDisplayedMonth] = useState(moment())
  const [applyNextTick, setApplyNextTick] = useState<boolean>()

  // initialize language stuff

  const MONTHS_LONG = getMonthsLong()
  const WEEKDAYS_LONG = getWeekdaysLong()
  const WEEKDAYS_SHORT = getWeekdaysShort()
  const MONTH_YEAR_OPTIONS = getMonthYearOptions()
  const START_MONTH = moment(first(MONTH_YEAR_OPTIONS)?.value)
  const END_MONTH = moment(last(MONTH_YEAR_OPTIONS)?.value)

  // initialize
  useEffect(() => {
    if (isRange) {
      let initialStart: Moment | null = get(value as TParseDateObject, 'from')
      if (!moment(initialStart).isValid()) {
        initialStart = null
      }
      let initialEnd: any = get(value, 'until')
      if (!moment(initialEnd).isValid()) {
        initialEnd = null
      }
      setStart(initialStart ? moment(initialStart) : undefined)
      setEnd(initialEnd ? moment(initialEnd) : undefined)
      setStartTime(initialStart ? moment(initialStart) : undefined)
      setEndTime(initialEnd ? moment(initialEnd) : undefined)
      if (initialEnd) {
        setHoveredDay(moment(initialEnd))
      }
      setDisplayedMonth(moment(initialStart))
      // TODO: set hovered/highlighted
    } else {
      let datum: Moment | null = value as Moment
      if (!moment(datum).isValid()) {
        datum = null
      }
      setStart(value ? moment(value as Moment) : undefined)
      setStartTime(value ? moment(value as Moment) : undefined)
      setDisplayedMonth(moment(value as Moment))
      // TODO: set hovered/highlighted
    }
  }, [value])

  const close = (): void => {
    onClose()
  }

  const apply = (): void => {
    // TODO: check isInfinite
    let from
    let until

    // global.log("apply", { start, isRange, end })

    // cancel
    if (!start || (isRange && !end)) {
      return
    }
    // console.log("apply start", { start, startTime })
    from = moment(start)
    if (startTime) {
      from.hour(startTime.hour())
      from.minute(startTime.minute())
    }
    from = from.toISOString()

    if (isRange) {
      until = moment(end)
      if (endTime) {
        until.hour(endTime.hour())
        until.minute(endTime.minute())
      }
      until = until.toISOString()
      onChange({ from, until })
    } else {
      onChange(from)
    }
    close()
  }

  useEffect(() => {
    if (applyNextTick) {
      setApplyNextTick(false)

      apply()
    }
  }, [applyNextTick])

  const getIsFirstDay = (day: Moment): boolean =>
    !start || (start && day.isBefore(start)) || Boolean(start && end)

  const setStartDay = (day: Moment | undefined): void => {
    if (!day) {
      setStart(undefined)
      return
    }
    setStart(day)
  }

  const setEndDay = (day: Moment | undefined): void => {
    if (!day) {
      setEnd(undefined)
      return
    }
    setEnd(day)
  }

  const changeMonth = (month: Date): void => {
    setDisplayedMonth(moment(month))
  }

  const handleDayClick = (
    dayAsDate: Date,
    { disabled }: DayModifiers,
  ): void => {
    if (disabled) return

    const day = moment(dayAsDate)

    if (!isRange) {
      setStartDay(day)
      setHoveredDay(undefined)
      if (autoclose && !timepicker) {
        setApplyNextTick(true)
      }
      return
    }

    const isFirstDay = getIsFirstDay(day)

    if (isFirstDay) {
      setStartDay(day)
      setEndDay(undefined)
      setHoveredDay(day)
    } else {
      setEndDay(day)
      setHoveredDay(day)
    }
  }

  const handleDayMouseEnter = (dayAsDate: Date): void => {
    const day = moment(dayAsDate)
    if (!isRange || getIsFirstDay(day)) {
      return
    }
    setHoveredDay(day)
  }

  const applyPreset = ({ start: presetStart, end: presetEnd }): void => {
    setStart(presetStart)
    setEnd(presetEnd)
    setStartTime(presetStart)
    setEndTime(presetEnd)
    setHoveredDay(presetEnd)
    setDisplayedMonth(presetStart)
  }

  const isInfinite = moment.unix(0).isSame(start)

  const selectedStart =
    start && moment(start).isValid() ? start.toDate() : undefined
  const selectedEnd =
    end && moment(end).isValid()
      ? end.toDate()
      : hoveredDay && moment(hoveredDay).isValid()
      ? hoveredDay.toDate()
      : undefined
  const selectedHovered =
    hoveredDay && moment(hoveredDay).isValid() ? hoveredDay.toDate() : undefined

  const modifiers = {
    start: selectedStart,
    end: isRange ? selectedEnd : selectedStart,
  }

  const disabledDays: BeforeAfterModifier = {
    before: undefined,
    after: undefined,
  } as any

  const selectedDays = [
    {
      from: start && moment(start).isValid() ? start.toDate() : undefined,
      to: isRange ? selectedHovered : selectedStart,
    },
  ]
  if (min) {
    disabledDays.before = moment(min).toDate()
  }
  if (max) {
    disabledDays.after = moment(max).toDate()
  }

  const setMonth =
    displayedMonth && displayedMonth.isValid && displayedMonth.isValid()
      ? displayedMonth.toDate()
      : moment().toDate()

  const clear = (): void => {
    onChange(isRange ? { from: null, until: null } : null)
    close()
  }

  return (
    <div className="d-flex flex-column">
      <div className="d-flex">
        <div>
          {isRange && (
            <Presets applyPreset={applyPreset} isInfinite={isInfinite} />
          )}
          <div className="mt-2 text-nowrap">
            <Button variant="success" onClick={apply} hidden={autoclose}>
              {i18n.t(`btn_ok`, `OK`)}
            </Button>
            <Button
              className="ml-1"
              variant="light"
              onClick={close}
              hidden={autoclose}
            >
              {i18n.t(`btn_cancel`, `Abbrechen`)}
            </Button>
            {clearable && (
              <Button1stlog
                icon="far fa-trash"
                className="ml-1"
                variant="light"
                onClick={clear}
                tooltip={i18n.t(`btn_clear`, `Zurücksetzen`)}
              ></Button1stlog>
            )}
          </div>
        </div>
        <div style={{ position: 'relative' }}>
          {isInfinite && (
            <Overlay>{i18n.t('unrestricted', 'Unbeschränkt')}</Overlay>
          )}
          {!autoclose && (
            <DateInput
              start={start as Moment}
              end={end as Moment}
              startTime={startTime as Moment}
              endTime={endTime as Moment}
              isRange={isRange}
              timepicker={timepicker}
            />
          )}
          {timepicker && (
            <TimePicker
              isRange={isRange}
              start={startTime as Moment}
              end={endTime as Moment}
              onChangeStart={setStartTime}
              onChangeEnd={setEndTime}
              selectedDays={selectedDays as any}
              minDate={min as Moment}
              maxDate={max as Moment}
              validateTime={validateTime}
            />
          )}
          <DayPicker
            // TODO: use browser locale
            locale="de"
            months={MONTHS_LONG}
            weekdaysLong={WEEKDAYS_LONG}
            weekdaysShort={WEEKDAYS_SHORT}
            firstDayOfWeek={1}
            month={setMonth}
            onMonthChange={changeMonth}
            className="Range"
            numberOfMonths={isRange ? 2 : 1}
            fromMonth={(min ? moment(min) : START_MONTH).toDate()}
            toMonth={(max ? moment(max) : END_MONTH).toDate()}
            selectedDays={selectedDays}
            disabledDays={nodisabled ? [] : disabledDays}
            modifiers={modifiers}
            onDayClick={handleDayClick}
            onDayMouseEnter={handleDayMouseEnter}
            navbarElement={CustomNavbar}
            captionElement={(captionProps) => (
              <MonthYearPicker {...captionProps} onChange={setDisplayedMonth} />
            )}
          />
        </div>
        <div>
          <div />
        </div>
      </div>
    </div>
  )
}

export default React.memo(DatePicker)
